import React, { useState, Fragment, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import api from '../../config/axios';
import Breadcrumb from '../common/breadcrumb';
import { useStateValue } from '../../store';

const NewReceipt = () => {
    const [{ auth }] = useStateValue();
    const [franchises, setFranchises] = useState([]);
    const [products, setProducts] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const [form, setForm] = useState({
        holderName: '',
        holderId: '',
        holderPhone: '',
        holderAddress: '',
        franchiseId: '5e95f51f-5b66-4c58-b2af-7a54e8b1df1e',
        type: 'prepago',
        currency: 'USD',
        paymentMethod: '',
        observation: '',
        email: '',
        clientId: '',
        purchaseId: '',
        subscriptionId: '',
    });

    const [items, setItems] = useState([
        { productId: '', name: '', quantity: 1, unitPrice: 0, currency: 'USD', subtotal: 0 },
    ]);

    useEffect(() => {
        fetchFranchises();
    }, []);

    // Cargar productos cuando se selecciona la franquicia
    useEffect(() => {
        if (form.franchiseId) {
            fetchProducts(form.franchiseId);
        }
    }, [form.franchiseId]);

    const fetchFranchises = async () => {
        try {
            const { data } = await api.get('/franchise');
            const franchiseData = data.data;
            // El endpoint puede retornar { count, rows } o un array directo
            const franchiseList = Array.isArray(franchiseData)
                ? franchiseData
                : franchiseData && franchiseData.rows
                ? franchiseData.rows
                : [];
            setFranchises(franchiseList);
        } catch (err) {
            toast.error('Error al cargar franquicias');
        }
    };

    const fetchProducts = async (franchiseId) => {
        if (!franchiseId) {
            setProducts([]);
            return;
        }
        try {
            const { data } = await api.get(`/franchise/${franchiseId}/products`);
            setProducts(data.data || []);
        } catch (err) {
            console.error('Error loading products:', err);
            setProducts([]);
        }
    };

    const currencySymbol = (currency) => {
        const symbols = { USD: '$', VES: 'Bs.', COP: '$' };
        return symbols[currency] || '$';
    };

    const handleFormChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleItemChange = (index, field, value) => {
        setItems((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };

            // Si cambió el producto, autocompletar nombre y precio
            if (field === 'productId') {
                const product = products.find((p) => p.id === value);
                if (product) {
                    const priceField =
                        form.currency === 'USD'
                            ? 'amount_usd'
                            : form.currency === 'COP'
                            ? 'amount_cop'
                            : product.amount_usd; // VES usa amount_usd como referencia
                    updated[index].name = product.name;
                    updated[index].unitPrice = parseFloat(product[priceField] || 0);
                }
            }

            // Recalcular subtotal
            const qty = parseFloat(updated[index].quantity) || 0;
            const price = parseFloat(updated[index].unitPrice) || 0;
            updated[index].subtotal = qty * price;
            updated[index].currency = form.currency;

            return updated;
        });
    };

    // Recalcular subtotales cuando cambia la moneda
    useEffect(() => {
        setItems((prev) =>
            prev.map((item) => {
                const product = products.find((p) => p.id === item.productId);
                let unitPrice = item.unitPrice;
                if (product) {
                    const priceField =
                        form.currency === 'USD'
                            ? 'amount_usd'
                            : form.currency === 'COP'
                            ? 'amount_cop'
                            : product.amount_usd;
                    unitPrice = parseFloat(product[priceField] || 0);
                }
                const qty = parseFloat(item.quantity) || 0;
                return {
                    ...item,
                    unitPrice,
                    currency: form.currency,
                    subtotal: qty * unitPrice,
                };
            }),
        );
    }, [form.currency, products]);

    const addItem = () => {
        setItems((prev) => [
            ...prev,
            { productId: '', name: '', quantity: 1, unitPrice: 0, currency: form.currency, subtotal: 0 },
        ]);
    };

    const removeItem = (index) => {
        setItems((prev) => prev.filter((_, i) => i !== index));
    };

    const totalAmount = items.reduce((sum, item) => sum + (parseFloat(item.subtotal) || 0), 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.holderName.trim()) {
            toast.error('El nombre del titular es requerido');
            return;
        }
        if (!form.franchiseId) {
            toast.error('Seleccione una franquicia');
            return;
        }
        if (items.length === 0 || items.every((i) => !i.productId && !i.name)) {
            toast.error('Agregue al menos un item');
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                holderName: form.holderName,
                holderId: form.holderId || null,
                holderPhone: form.holderPhone || null,
                holderAddress: form.holderAddress || null,
                franchiseId: form.franchiseId,
                type: form.type,
                currency: form.currency,
                paymentMethod: form.paymentMethod || null,
                observation: form.observation || null,
                email: form.email || null,
                clientId: form.clientId || null,
                purchaseId: form.purchaseId || null,
                subscriptionId: form.subscriptionId || null,
                items: items
                    .filter((i) => i.name || i.productId)
                    .map((i) => ({
                        productId: i.productId || null,
                        name: i.name,
                        quantity: parseFloat(i.quantity) || 1,
                        unitPrice: parseFloat(i.unitPrice) || 0,
                        currency: form.currency,
                        subtotal: parseFloat(i.subtotal) || 0,
                    })),
                totalAmount: parseFloat(totalAmount.toFixed(2)),
            };

            const { data } = await api.post('/receipt', payload);
            if (data.success) {
                toast.success(`Recibo #${data.data.receiptNumber} creado`);
                window.location.href = `${process.env.PUBLIC_URL}/recibos/${data.data.id}`;
            }
        } catch (err) {
            toast.error('Error al crear el recibo');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Fragment>
            <Breadcrumb title="Nuevo recibo" parent="Recibos" />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5>Nuevo recibo</h5>
                                    <Link
                                        to={`${process.env.PUBLIC_URL}/recibos`}
                                        className="btn btn-light btn-sm"
                                    >
                                        <i className="fa fa-arrow-left mr-1"></i>
                                        Volver
                                    </Link>
                                </div>
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label>
                                                    Nombre del titular <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={form.holderName}
                                                    onChange={(e) => handleFormChange('holderName', e.target.value)}
                                                    placeholder="Nombre que aparece en el recibo"
                                                />
                                                <small className="form-text text-muted">
                                                    No necesariamente es el mismo nombre del cliente
                                                </small>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <input
                                                type="hidden"
                                                value={form.type}
                                                onChange={(e) => handleFormChange('type', e.target.value)}
                                            />
                                            <input
                                                type="hidden"
                                                value={form.franchiseId}
                                                onChange={(e) => handleFormChange('franchiseId', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label>Identificación (RIF o cédula)</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={form.holderId}
                                                    onChange={(e) => handleFormChange('holderId', e.target.value)}
                                                    placeholder="Ej: V-12345678, J-12345678-9"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label>Teléfono</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={form.holderPhone}
                                                    onChange={(e) => handleFormChange('holderPhone', e.target.value)}
                                                    placeholder="Ej: 0414-7353821"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label>Dirección (opcional)</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={form.holderAddress}
                                                    onChange={(e) => handleFormChange('holderAddress', e.target.value)}
                                                    placeholder="Dirección del titular"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label>Moneda</label>
                                                <select
                                                    className="form-control"
                                                    value={form.currency}
                                                    onChange={(e) => handleFormChange('currency', e.target.value)}
                                                >
                                                    <option value="USD">USD ($)</option>
                                                    <option value="VES">VES (Bs.)</option>
                                                    <option value="COP">COP ($)</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label>Método de pago</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={form.paymentMethod}
                                                    onChange={(e) => handleFormChange('paymentMethod', e.target.value)}
                                                    placeholder="Ej: Pago Móvil, Efectivo COP..."
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label>Correo para envío (opcional)</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    value={form.email}
                                                    onChange={(e) => handleFormChange('email', e.target.value)}
                                                    placeholder="cliente@ejemplo.com"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <hr />

                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6>Items del recibo</h6>
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={addItem}
                                        >
                                            <i className="fa fa-plus mr-1"></i>
                                            Agregar item
                                        </button>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Producto</th>
                                                    <th style={{ width: '80px' }}>Cant.</th>
                                                    <th style={{ width: '120px' }}>Precio</th>
                                                    <th style={{ width: '120px' }}>Subtotal</th>
                                                    <th style={{ width: '50px' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <select
                                                                className="form-control form-control-sm"
                                                                value={item.productId}
                                                                onChange={(e) =>
                                                                    handleItemChange(index, 'productId', e.target.value)
                                                                }
                                                            >
                                                                <option value="">Personalizado...</option>
                                                                {products.map((p) => (
                                                                    <option key={p.id} value={p.id}>
                                                                        {p.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {!item.productId && (
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm mt-1"
                                                                    value={item.name}
                                                                    onChange={(e) =>
                                                                        handleItemChange(index, 'name', e.target.value)
                                                                    }
                                                                    placeholder="Nombre del producto"
                                                                />
                                                            )}
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                className="form-control form-control-sm"
                                                                value={item.quantity}
                                                                onChange={(e) =>
                                                                    handleItemChange(index, 'quantity', e.target.value)
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                className="form-control form-control-sm"
                                                                value={item.unitPrice}
                                                                onChange={(e) =>
                                                                    handleItemChange(index, 'unitPrice', e.target.value)
                                                                }
                                                            />
                                                        </td>
                                                        <td className="text-right font-weight-bold align-middle">
                                                            {currencySymbol(form.currency)}{' '}
                                                            {Number(item.subtotal || 0).toFixed(2)}
                                                        </td>
                                                        <td className="text-center align-middle">
                                                            {items.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-danger btn-sm"
                                                                    onClick={() => removeItem(index)}
                                                                >
                                                                    <i className="fa fa-times"></i>
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="3" className="text-right font-weight-bold">
                                                        Total:
                                                    </td>
                                                    <td className="text-right font-weight-bold" style={{ fontSize: 18 }}>
                                                        {currencySymbol(form.currency)}{' '}
                                                        {Number(totalAmount).toFixed(2)}
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>

                                    <div className="form-group mt-3">
                                        <label>Observación (opcional)</label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            value={form.observation}
                                            onChange={(e) => handleFormChange('observation', e.target.value)}
                                            placeholder="Notas adicionales..."
                                        />
                                    </div>

                                    <div className="d-flex justify-content-end mt-4">
                                        <Link
                                            to={`${process.env.PUBLIC_URL}/recibos`}
                                            className="btn btn-light mr-2"
                                        >
                                            Cancelar
                                        </Link>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={submitting}
                                        >
                                            {submitting ? (
                                                <span className="spinner-border spinner-border-sm mr-1" />
                                            ) : (
                                                <i className="fa fa-save mr-1"></i>
                                            )}
                                            {submitting ? 'Guardando...' : 'Crear recibo'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default NewReceipt;
